/* Space Calculator — pure calculation engine + SVG rendering.
   No DOM access; deterministic output from inputs. */

// ─── Types ───────────────────────────────────────────────────────

export interface BoxType {
  id: string;
  name: string;
  length: number;   // inches
  width: number;    // inches
  height: number;   // inches
  weight: number;   // ounces
  quantity: number;
}

export interface FloorArea {
  id: string;
  name: string;
  length: number;   // feet
  width: number;    // feet
  height: number;   // feet
  boxTypeId: string;
  needsAisle: boolean;
}

export interface StackingSettings {
  aisleWidth: number;      // inches
  maxStackHeight: number;  // boxes
  stabilityRatio: number;  // max height:row-width
  rowDepth: number;        // rows between aisles
}

interface RowSpan {
  perpStart: number;
  perpEnd: number;
}

export interface Layout {
  totalBoxes: number;
  totalStacks: number;
  stackHeight: number;
  totalRows: number;
  boxesPerRow: number;
  nUnits: number;
  extraRows: number;
  unitRows: number;
  boxAlong: number;
  boxPerp: number;
  floorAlong: number;
  floorPerp: number;
  aisleDir: "length" | "width";
  aisleWidth: number;
  alongOffset: number;
  rows: RowSpan[];
  aisles: RowSpan[];
  usedAlong: number;
  wastedAlong: number;
  wastedPerp: number;
  stabilityLimit: number;
  physicalLimit: number;
  floorL: number;
  floorW: number;
  floorH: number;
  boxH: number;
}

interface ArrangementCombo {
  boxAlong: number;
  boxPerp: number;
  floorAlong: number;
  floorPerp: number;
  aisleDir: "length" | "width";
}

type PartialLayout = Omit<Layout, "floorL" | "floorW" | "floorH" | "boxH">;

// ─── ID generation ──────────────────────────────────────────────

let nextId = 0;
export function genId(): string {
  return "sc-" + Date.now().toString(36) + "-" + (nextId++).toString(36);
}

// ─── Defaults ────────────────────────────────────────────────────

export const DEFAULT_SETTINGS: StackingSettings = {
  aisleWidth: 24,
  maxStackHeight: 5,
  stabilityRatio: 3.0,
  rowDepth: 6,
};

export const DEFAULT_BOX_TYPES: Omit<BoxType, "id">[] = [
  { name: "Box-30",  length: 22, width: 12.5, height: 8,   weight: 342, quantity: 912 },
  { name: "Box-300", length: 24, width: 12,   height: 7.5, weight: 440, quantity: 257 },
];

export const DEFAULT_FLOOR_AREAS: { name: string; length: number; width: number; height: number; boxTypeIndex: number; needsAisle: boolean }[] = [
  { name: "Area 1 (Sample Area)", length: 20, width: 11, height: 8, boxTypeIndex: 0, needsAisle: true },
  { name: "Area 2 (Lounge)",     length: 14, width: 9,  height: 8, boxTypeIndex: 0, needsAisle: true },
  { name: "Area 3 (Kitchen)",    length: 9,  width: 9,  height: 8, boxTypeIndex: 1, needsAisle: true },
];

export const NEW_BOX_DEFAULTS: Omit<BoxType, "id"> = {
  name: "New Box",
  length: 24,
  width: 12,
  height: 8,
  weight: 300,
  quantity: 100,
};

export const NEW_FLOOR_AREA_DEFAULTS = {
  length: 10,
  width: 8,
  height: 8,
};

export const BOX_COLORS = [
  "#f59e0b", "#0891b2", "#8b5cf6", "#ef4444",
  "#10b981", "#ec4899", "#6366f1", "#14b8a6",
];

// ─── Calculation ─────────────────────────────────────────────────

export function calculateLayout(
  floorArea: FloorArea,
  boxType: BoxType,
  settings: StackingSettings,
): Layout | null {
  const floorL = floorArea.length * 12;
  const floorW = floorArea.width * 12;
  const floorH = floorArea.height * 12;
  const { length: boxL, width: boxW, height: boxH } = boxType;
  const aisleW = floorArea.needsAisle ? settings.aisleWidth : 0;
  const { rowDepth: maxDepth } = settings;

  if (floorL <= 0 || floorW <= 0 || floorH <= 0) return null;
  if (boxL <= 0 || boxW <= 0 || boxH <= 0) return null;

  const combos: ArrangementCombo[] = [
    { boxAlong: boxL, boxPerp: boxW, floorAlong: floorL, floorPerp: floorW, aisleDir: "length" },
    { boxAlong: boxW, boxPerp: boxL, floorAlong: floorL, floorPerp: floorW, aisleDir: "length" },
    { boxAlong: boxL, boxPerp: boxW, floorAlong: floorW, floorPerp: floorL, aisleDir: "width" },
    { boxAlong: boxW, boxPerp: boxL, floorAlong: floorW, floorPerp: floorL, aisleDir: "width" },
  ];

  let best: PartialLayout | null = null;
  for (const c of combos) {
    const layout = computeArrangement(c, floorH, boxH, aisleW, settings.maxStackHeight, settings.stabilityRatio, maxDepth);
    if (layout && (!best || layout.totalBoxes > best.totalBoxes)) {
      best = layout;
    }
  }

  if (!best) return null;
  return { ...best, floorL, floorW, floorH, boxH: boxType.height };
}

function computeArrangement(
  c: ArrangementCombo,
  floorH: number,
  boxH: number,
  aisleWidth: number,
  maxStackH: number,
  stabilityRatio: number,
  maxDepth: number,
): PartialLayout | null {
  const stabilityLimit = Math.floor(stabilityRatio * c.boxPerp / boxH);
  const physicalLimit = Math.floor(floorH / boxH);
  const stackHeight = Math.min(physicalLimit, maxStackH, stabilityLimit);
  if (stackHeight <= 0) return null;

  const effectiveAlong = c.floorAlong - 2 * aisleWidth;
  const boxesPerRow = Math.floor(effectiveAlong / c.boxAlong);
  if (boxesPerRow <= 0) return null;

  const unitRows = maxDepth;
  const unitWidth = unitRows * c.boxPerp + aisleWidth;

  const nUnits = Math.max(0, Math.floor((c.floorPerp - aisleWidth) / unitWidth));
  const usedPerp = aisleWidth + nUnits * unitWidth;
  const remaining = c.floorPerp - usedPerp;
  const extraRowSpace = remaining - aisleWidth;
  const extraRows = extraRowSpace > 0
    ? Math.min(Math.floor(extraRowSpace / c.boxPerp), unitRows)
    : 0;

  const totalRows = nUnits * unitRows + extraRows;
  if (totalRows <= 0) return null;

  const alongOffset = aisleWidth;

  const rows: RowSpan[] = [];
  for (let i = 0; i < nUnits; i++) {
    for (let r = 0; r < unitRows; r++) {
      rows.push({
        perpStart: i * unitWidth + aisleWidth + r * c.boxPerp,
        perpEnd: i * unitWidth + aisleWidth + (r + 1) * c.boxPerp,
      });
    }
  }
  const extraStart = nUnits * unitWidth + aisleWidth;
  for (let r = 0; r < extraRows; r++) {
    rows.push({
      perpStart: extraStart + r * c.boxPerp,
      perpEnd: extraStart + (r + 1) * c.boxPerp,
    });
  }

  const perpAisles: RowSpan[] = [];
  for (let i = 0; i <= nUnits; i++) {
    perpAisles.push({
      perpStart: i * unitWidth,
      perpEnd: i * unitWidth + aisleWidth,
    });
  }
  if (extraRows > 0) {
    const trailingStart = extraStart + extraRows * c.boxPerp;
    perpAisles.push({ perpStart: trailingStart, perpEnd: trailingStart + aisleWidth });
  }

  const totalStacks = totalRows * boxesPerRow;
  const totalBoxes = totalStacks * stackHeight;

  const usedAlong = boxesPerRow * c.boxAlong;
  const usedPerpTotal = rows.length > 0 ? rows[rows.length - 1].perpEnd : 0;
  const wastedAlong = effectiveAlong - usedAlong;
  const wastedPerp = c.floorPerp - (usedPerpTotal + aisleWidth);

  return {
    totalBoxes,
    totalStacks,
    stackHeight,
    totalRows,
    boxesPerRow,
    nUnits,
    extraRows,
    unitRows,
    boxAlong: c.boxAlong,
    boxPerp: c.boxPerp,
    floorAlong: c.floorAlong,
    floorPerp: c.floorPerp,
    aisleDir: c.aisleDir,
    aisleWidth,
    alongOffset,
    rows,
    aisles: perpAisles,
    usedAlong,
    wastedAlong,
    wastedPerp,
    stabilityLimit,
    physicalLimit,
  };
}

// ─── SVG Rendering ───────────────────────────────────────────────

export function fmtDim(ft: number): string {
  const whole = Math.floor(ft);
  const frac = ft - whole;
  const inches = Math.round(frac * 12);
  if (inches === 0) return `${whole}ft`;
  return `${whole}ft ${inches}in`;
}

export function renderTopViewSVG(layout: Layout | null, boxColor: string): string {
  if (!layout) return "";
  const { floorL, floorW } = layout;
  const pad = 45;
  const maxContentWidth = 560;

  const scale = Math.min(maxContentWidth / floorL, 400 / floorW);
  const cw = floorL * scale;
  const ch = floorW * scale;
  const svgW = cw + pad * 2;
  const svgH = ch + pad * 2 + 14;

  const ox = pad;
  const oy = pad;

  let svg = `<svg width="${svgW}" height="${svgH}" viewBox="0 0 ${svgW} ${svgH}" xmlns="http://www.w3.org/2000/svg" style="font-family:-apple-system,BlinkMacSystemFont,sans-serif">`;
  svg += `<rect x="${ox}" y="${oy}" width="${cw}" height="${ch}" fill="#f3f4f6" stroke="#4b5563" stroke-width="1.5" rx="2"/>`;

  const aislesOnL = layout.aisleDir === "length";
  const ao = layout.alongOffset || 0;

  for (const aisle of layout.aisles) {
    if (aislesOnL) {
      const ay = oy + aisle.perpStart * scale;
      const ah = (aisle.perpEnd - aisle.perpStart) * scale;
      svg += `<rect x="${ox}" y="${ay}" width="${cw}" height="${ah}" fill="#dbeafe" rx="1"/>`;
      if (ah > 14) {
        svg += `<text x="${ox + cw / 2}" y="${ay + ah / 2 + 3.5}" text-anchor="middle" font-size="9" fill="#6b7280" opacity="0.7">${layout.aisleWidth}" aisle</text>`;
      }
    } else {
      const ax = ox + aisle.perpStart * scale;
      const aw = (aisle.perpEnd - aisle.perpStart) * scale;
      svg += `<rect x="${ax}" y="${oy}" width="${aw}" height="${ch}" fill="#dbeafe" rx="1"/>`;
      if (aw > 14) {
        svg += `<text x="${ax + aw / 2}" y="${oy + ch / 2}" text-anchor="middle" font-size="9" fill="#6b7280" opacity="0.7" transform="rotate(-90 ${ax + aw / 2} ${oy + ch / 2})">${layout.aisleWidth}" aisle</text>`;
      }
    }
  }

  if (aislesOnL) {
    const nearW = ao * scale;
    const farX = ox + (ao + layout.boxesPerRow * layout.boxAlong) * scale;
    const farW = cw - farX + ox;
    svg += `<rect x="${ox}" y="${oy}" width="${nearW}" height="${ch}" fill="#dbeafe" rx="1"/>`;
    svg += `<rect x="${farX}" y="${oy}" width="${farW}" height="${ch}" fill="#dbeafe" rx="1"/>`;
  } else {
    const nearH = ao * scale;
    const farY = oy + (ao + layout.boxesPerRow * layout.boxAlong) * scale;
    const farH = ch - farY + oy;
    svg += `<rect x="${ox}" y="${oy}" width="${cw}" height="${nearH}" fill="#dbeafe" rx="1"/>`;
    svg += `<rect x="${ox}" y="${farY}" width="${cw}" height="${farH}" fill="#dbeafe" rx="1"/>`;
  }

  for (const row of layout.rows) {
    for (let j = 0; j < layout.boxesPerRow; j++) {
      let rx: number, ry: number, rw: number, rh: number;
      if (aislesOnL) {
        rx = ox + (ao + j * layout.boxAlong) * scale;
        ry = oy + row.perpStart * scale;
        rw = layout.boxAlong * scale;
        rh = layout.boxPerp * scale;
      } else {
        rx = ox + row.perpStart * scale;
        ry = oy + (ao + j * layout.boxAlong) * scale;
        rw = layout.boxPerp * scale;
        rh = layout.boxAlong * scale;
      }
      svg += `<rect x="${rx}" y="${ry}" width="${rw}" height="${rh}" fill="${boxColor}" stroke="#fff" stroke-width="0.6" rx="1" opacity="0.85"/>`;
    }
  }

  const dimColor = "#4b5563";
  const dimFontSize = 10;
  const lblL = fmtDim(floorL / 12);
  const lblW = fmtDim(floorW / 12);

  svg += `<text x="${ox + cw / 2}" y="${oy + ch + 18}" text-anchor="middle" font-size="${dimFontSize}" fill="${dimColor}">${lblL}</text>`;
  svg += `<text x="${ox - 10}" y="${oy + ch / 2}" text-anchor="middle" font-size="${dimFontSize}" fill="${dimColor}" transform="rotate(-90 ${ox - 10} ${oy + ch / 2})">${lblW}</text>`;

  svg += "</svg>";
  return svg;
}

export function renderSideViewSVG(layout: Layout | null, boxColor: string): string {
  if (!layout) return "";
  const ceilingH = layout.floorH;
  const { boxH } = layout;
  const { stackHeight } = layout;
  const totalH = stackHeight * boxH;

  const pad = 20;
  const maxH = 130;
  const scaleY = maxH / ceilingH;
  const boxDrawW = 50;
  const svgW = boxDrawW + pad * 2 + 40;
  const svgH = maxH + pad * 2;

  const ox = pad + 20;
  const floorY = pad + maxH;

  let svg = `<svg width="${svgW}" height="${svgH}" viewBox="0 0 ${svgW} ${svgH}" xmlns="http://www.w3.org/2000/svg" style="font-family:-apple-system,BlinkMacSystemFont,sans-serif">`;

  svg += `<line x1="${ox - 5}" y1="${pad}" x2="${ox + boxDrawW + 5}" y2="${pad}" stroke="#9ca3af" stroke-width="1" stroke-dasharray="4 2"/>`;
  svg += `<text x="${ox + boxDrawW + 8}" y="${pad + 3}" font-size="8" fill="#9ca3af">ceiling</text>`;

  svg += `<line x1="${ox - 5}" y1="${floorY}" x2="${ox + boxDrawW + 5}" y2="${floorY}" stroke="#374151" stroke-width="2"/>`;

  for (let i = 0; i < stackHeight; i++) {
    const by = floorY - (i + 1) * boxH * scaleY;
    const bh = boxH * scaleY;
    svg += `<rect x="${ox}" y="${by}" width="${boxDrawW}" height="${bh}" fill="${boxColor}" stroke="#fff" stroke-width="1" rx="2" opacity="0.85"/>`;
  }

  const topOfStack = floorY - totalH * scaleY;
  svg += `<line x1="${ox - 12}" y1="${floorY}" x2="${ox - 12}" y2="${topOfStack}" stroke="#374151" stroke-width="1"/>`;
  svg += `<line x1="${ox - 15}" y1="${floorY}" x2="${ox - 9}" y2="${floorY}" stroke="#374151" stroke-width="1"/>`;
  svg += `<line x1="${ox - 15}" y1="${topOfStack}" x2="${ox - 9}" y2="${topOfStack}" stroke="#374151" stroke-width="1"/>`;
  svg += `<text x="${ox - 13}" y="${(floorY + topOfStack) / 2 + 3}" text-anchor="end" font-size="9" fill="#374151">${totalH}"</text>`;

  svg += "</svg>";
  return svg;
}

// ─── Config Import / Export ─────────────────────────────────────

const CONFIG_VERSION = 1 as const;

export interface SpaceCalculatorConfig {
  version: typeof CONFIG_VERSION;
  exportedAt: string;
  settings: StackingSettings;
  boxTypes: BoxType[];
  floorAreas: FloorArea[];
}

export function buildConfig(
  settings: StackingSettings,
  boxTypes: BoxType[],
  floorAreas: FloorArea[],
): SpaceCalculatorConfig {
  return {
    version: CONFIG_VERSION,
    exportedAt: new Date().toISOString(),
    settings,
    boxTypes,
    floorAreas,
  };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function validateStackingSettings(v: unknown): v is StackingSettings {
  if (!isRecord(v)) return false;
  return (
    typeof v.aisleWidth === "number" &&
    typeof v.maxStackHeight === "number" &&
    typeof v.stabilityRatio === "number" &&
    typeof v.rowDepth === "number"
  );
}

function validateBoxType(v: unknown): v is BoxType {
  if (!isRecord(v)) return false;
  return (
    typeof v.id === "string" &&
    typeof v.name === "string" &&
    typeof v.length === "number" &&
    typeof v.width === "number" &&
    typeof v.height === "number" &&
    typeof v.weight === "number" &&
    typeof v.quantity === "number"
  );
}

function validateFloorArea(v: unknown): v is FloorArea {
  if (!isRecord(v)) return false;
  return (
    typeof v.id === "string" &&
    typeof v.name === "string" &&
    typeof v.length === "number" &&
    typeof v.width === "number" &&
    typeof v.height === "number" &&
    typeof v.boxTypeId === "string" &&
    typeof v.needsAisle === "boolean"
  );
}

export interface ConfigValidationResult {
  ok: true;
  config: SpaceCalculatorConfig;
}

export interface ConfigValidationError {
  ok: false;
  error: string;
}

export function parseAndValidateConfig(
  raw: string,
): ConfigValidationResult | ConfigValidationError {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, error: "File is not valid JSON." };
  }

  if (!isRecord(parsed)) {
    return { ok: false, error: "Config must be a JSON object." };
  }

  if (parsed.version !== CONFIG_VERSION) {
    return {
      ok: false,
      error: `Unsupported config version "${String(parsed.version)}". Expected ${CONFIG_VERSION}.`,
    };
  }

  if (typeof parsed.exportedAt !== "string") {
    return { ok: false, error: 'Missing or invalid "exportedAt" field.' };
  }

  if (!validateStackingSettings(parsed.settings)) {
    return { ok: false, error: 'Invalid or missing "settings" object.' };
  }

  if (!Array.isArray(parsed.boxTypes) || parsed.boxTypes.length === 0) {
    return { ok: false, error: '"boxTypes" must be a non-empty array.' };
  }
  for (let i = 0; i < parsed.boxTypes.length; i++) {
    if (!validateBoxType(parsed.boxTypes[i])) {
      return { ok: false, error: `Invalid box type at index ${i}.` };
    }
  }

  if (!Array.isArray(parsed.floorAreas) || parsed.floorAreas.length === 0) {
    return { ok: false, error: '"floorAreas" must be a non-empty array.' };
  }
  for (let i = 0; i < parsed.floorAreas.length; i++) {
    if (!validateFloorArea(parsed.floorAreas[i])) {
      return { ok: false, error: `Invalid floor area at index ${i}.` };
    }
  }

  const validBoxTypes = parsed.boxTypes as BoxType[];
  const validFloorAreas = parsed.floorAreas as FloorArea[];

  const boxTypeIds = new Set(validBoxTypes.map((bt) => bt.id));
  for (const fa of validFloorAreas) {
    if (!boxTypeIds.has(fa.boxTypeId)) {
      return {
        ok: false,
        error: `Floor area "${fa.name}" references unknown box type id "${fa.boxTypeId}".`,
      };
    }
  }

  return {
    ok: true,
    config: {
      version: CONFIG_VERSION,
      exportedAt: parsed.exportedAt as string,
      settings: parsed.settings as StackingSettings,
      boxTypes: validBoxTypes,
      floorAreas: validFloorAreas,
    },
  };
}
